import { useEffect, useMemo, useRef } from "react";
import type { Point, RainLevel, WaterType } from "../types";
import { FIXED_WATER_PALETTE, waterColorAsGlsl } from "../lib/water-palette";

interface Bounds { left: number; top: number; width: number; height: number }

interface Props {
  regions: RenderedWaterRegion[];
  weatherDryPolygons?: Point[][];
  bounds: Bounds;
  rain?: RainLevel;
  paused?: boolean;
  projectionMask?: string;
}

export interface RenderedWaterRegion {
  id: string;
  polygons: Point[][];
  erasePolygons: Point[][];
  flowPaths: Point[][];
  brushRadii: number[];
  waterType: WaterType;
  flowDirection: number;
  movement: number;
  opacity: number;
}

const RAIN_REACTION: Record<RainLevel, number> = { off: 0, light: 0.34, medium: 0.67, heavy: 1 };

const VERTEX_SHADER = `
attribute vec2 a_position;
varying vec2 v_uv;
void main() {
  v_uv = a_position * 0.5 + 0.5;
  gl_Position = vec4(a_position, 0.0, 1.0);
}`;

// A light-weight top-down interpretation of an ocean surface: intersecting
// wave trains perturb a virtual normal, which drives moving highlights,
// shallow caustics, and darker troughs without requiring a texture download.
const FRAGMENT_SHADER = `
precision highp float;
varying vec2 v_uv;
uniform float u_time;
uniform vec2 u_resolution;
uniform float u_rain;
uniform sampler2D u_weather_mask;
uniform sampler2D u_water_settings;

float hash(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

float noise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1.0, 0.0)), f.x),
             mix(hash(i + vec2(0.0, 1.0)), hash(i + vec2(1.0)), f.x), f.y);
}

float oceanWaves(vec2 p, float time, float movement) {
  float motion = mix(0.05, 1.25, movement);
  float broad = sin(p.x * 3.1 + p.y * 1.15 + time * 0.72 * motion) * 0.34;
  broad += sin(-p.x * 1.7 + p.y * 3.8 - time * 0.54 * motion) * 0.25;
  broad += sin(p.x * 5.8 - p.y * 2.25 + time * 0.91 * motion) * 0.13;
  float chop = noise(p * 4.2 + vec2(time * 0.22, -time * 0.16) * motion) - 0.5;
  return (broad + chop * 0.28) * mix(0.12, 1.0, movement);
}

float riverWaves(vec2 p, vec2 flow, float time, float movement) {
  vec2 direction = flow / max(length(flow), 0.001);
  vec2 across = vec2(-direction.y, direction.x);
  float alongCoordinate = dot(p, direction);
  float acrossCoordinate = dot(p, across);
  float speed = mix(0.08, 2.3, movement);
  float stream = sin(acrossCoordinate * 6.2 + alongCoordinate * 0.55 - time * speed) * 0.22;
  stream += sin(acrossCoordinate * 11.5 - alongCoordinate * 0.36 - time * speed * 1.45) * 0.11;
  float currentNoise = noise((p - direction * time * speed * 0.52) * vec2(3.4, 5.1)) - 0.5;
  return (stream + currentNoise * 0.24) * mix(0.10, 1.0, movement);
}

float rainRippleLayer(vec2 uv, float columns, float speed, float seed) {
  float aspect = u_resolution.x / max(u_resolution.y, 1.0);
  vec2 p = uv * vec2(columns * aspect, columns);
  vec2 id = floor(p);
  vec2 cell = fract(p) - 0.5;
  float random = hash(id + seed);
  float phase = fract(u_time * speed + random * 6.71);
  float present = step(1.0 - u_rain * 0.78, hash(id + seed * 3.17));
  vec2 center = vec2(hash(id + seed * 5.73), hash(id.yx + seed * 8.91)) - 0.5;
  float distanceToImpact = length(cell - center);
  float radius = 0.018 + phase * 0.31;
  float ring = 1.0 - smoothstep(0.018, 0.048, abs(distanceToImpact - radius));
  float impact = 1.0 - smoothstep(0.012, 0.060, distanceToImpact);
  return present * (ring * pow(1.0 - phase, 1.8) + impact * exp(-phase * 28.0) * 0.75);
}

void main() {
  vec4 waterSettings = texture2D(u_water_settings, v_uv);
  if (waterSettings.a < 0.01) discard;
  float encodedSettings = floor(waterSettings.b * 255.0 + 0.5);
  float movement = floor(encodedSettings / 32.0) / 7.0;
  float opacity = mod(encodedSettings, 32.0) / 31.0;
  vec2 encodedFlow = vec2(waterSettings.r * 2.0 - 1.0, -(waterSettings.g * 2.0 - 1.0));
  float flowMagnitude = length(encodedFlow);
  vec2 flow = encodedFlow / max(flowMagnitude, 0.001);
  float isRiver = step(0.72, flowMagnitude);
  vec2 aspect = vec2(max(u_resolution.x / max(u_resolution.y, 1.0), 1.0), max(u_resolution.y / max(u_resolution.x, 1.0), 1.0));
  vec2 p = (v_uv - 0.5) * aspect * 5.4;
  float e = 0.025;
  vec2 acrossFlow = vec2(-flow.y, flow.x);
  vec2 oceanP = vec2(dot(p, flow), dot(p, acrossFlow));
  vec2 oceanPx = vec2(dot(p + vec2(e, 0.0), flow), dot(p + vec2(e, 0.0), acrossFlow));
  vec2 oceanPy = vec2(dot(p + vec2(0.0, e), flow), dot(p + vec2(0.0, e), acrossFlow));
  float h = mix(oceanWaves(oceanP, u_time, movement), riverWaves(p, flow, u_time, movement), isRiver);
  float hx = mix(oceanWaves(oceanPx, u_time, movement), riverWaves(p + vec2(e, 0.0), flow, u_time, movement), isRiver) - h;
  float hy = mix(oceanWaves(oceanPy, u_time, movement), riverWaves(p + vec2(0.0, e), flow, u_time, movement), isRiver) - h;
  vec3 normal = normalize(vec3(-hx * 7.5, -hy * 7.5, e));
  vec3 sun = normalize(vec3(-0.45, -0.55, 0.75));
  float glint = pow(max(dot(normal, sun), 0.0), 22.0);
  float softLight = max(dot(normal, normalize(vec3(0.25, -0.2, 0.95))), 0.0);
  float caustic = smoothstep(0.36, 0.72, noise(p * 3.8 + normal.xy * 2.1 + vec2(u_time * 0.15, u_time * 0.09)));
  float crest = smoothstep(0.32, 0.62, h + length(vec2(hx, hy)) * 2.4);
  // The live surface uses the exact fixed-water palette. Motion contributes
  // restrained slate highlights instead of repainting the portable artwork
  // beneath it with a bright cyan film.
  vec3 deep = ${waterColorAsGlsl(FIXED_WATER_PALETTE.deep)};
  vec3 base = ${waterColorAsGlsl(FIXED_WATER_PALETTE.base)};
  vec3 middle = ${waterColorAsGlsl(FIXED_WATER_PALETTE.middle)};
  vec3 ripple = ${waterColorAsGlsl(FIXED_WATER_PALETTE.ripple)};
  vec3 highlight = ${waterColorAsGlsl(FIXED_WATER_PALETTE.highlight)};
  vec3 color = mix(deep, base, 0.58 + softLight * 0.18);
  color = mix(color, middle, caustic * 0.16);
  color += ripple * glint * 0.16;
  color += highlight * crest * 0.018;
  float weatherAllowed = texture2D(u_weather_mask, v_uv).r;
  float rainRipples = 0.0;
  if (u_rain > 0.01 && weatherAllowed > 0.01) {
    rainRipples += rainRippleLayer(v_uv, 20.0, 1.55, 1.7) * 0.72;
    rainRipples += rainRippleLayer(v_uv, 34.0, 2.30, 6.4) * 0.58;
    rainRipples += rainRippleLayer(v_uv, 48.0, 3.05, 11.2) * 0.42;
    rainRipples *= u_rain * weatherAllowed;
  }
  color += ripple * min(rainRipples, 1.0) * (0.12 + u_rain * 0.12);
  float alpha = clamp((0.30 + crest * 0.035 + min(rainRipples, 0.09)) * opacity, 0.0, 0.52);
  gl_FragColor = vec4(color, alpha);
}`;

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Unable to allocate the water shader.");
  gl.shaderSource(shader, source); gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) { const message = gl.getShaderInfoLog(shader) ?? "Water shader failed to compile."; gl.deleteShader(shader); throw new Error(message); }
  return shader;
}

function drawWeatherMask(polygons: Point[][], cssWidth: number, cssHeight: number) {
  const maxSide = 1024;
  const scale = Math.min(1, maxSide / Math.max(cssWidth, cssHeight, 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cssWidth * scale)); canvas.height = Math.max(1, Math.round(cssHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) return canvas;
  context.fillStyle = "#fff"; context.fillRect(0, 0, canvas.width, canvas.height);
  context.fillStyle = "#000";
  for (const polygon of polygons) {
    if (polygon.length < 3) continue;
    context.beginPath();
    polygon.forEach((point, index) => { const x = point.x * scale; const y = point.y * scale; if (index) context.lineTo(x, y); else context.moveTo(x, y); });
    context.closePath(); context.fill();
  }
  return canvas;
}

const byte = (value: number) => Math.max(0, Math.min(255, Math.round(value)));
const setting = (value: number) => Math.max(0, Math.min(1, Number.isFinite(value) ? value : 0));

function flowColor(path: Point[], waterType: WaterType, movement: number, opacity: number, flowDirection: number) {
  const first = path[0] ?? { x: 0, y: 0 }; const last = path.at(-1) ?? { x: first.x + 1, y: first.y };
  const radians = Number.isFinite(flowDirection) ? flowDirection * Math.PI / 180 : Math.atan2(last.y - first.y, last.x - first.x);
  const magnitude = waterType === "river" ? 0.5 : 0.25;
  const red = byte((Math.cos(radians) * magnitude + 0.5) * 255); const green = byte((Math.sin(radians) * magnitude + 0.5) * 255);
  const packed = Math.round(setting(movement) * 7) * 32 + Math.round(setting(opacity) * 31);
  return `rgb(${red} ${green} ${packed})`;
}

function drawWaterSettings(regions: RenderedWaterRegion[], cssWidth: number, cssHeight: number) {
  const maxSide = 1024;
  const scale = Math.min(1, maxSide / Math.max(cssWidth, cssHeight, 1));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(cssWidth * scale)); canvas.height = Math.max(1, Math.round(cssHeight * scale));
  const context = canvas.getContext("2d");
  if (!context) return canvas;
  for (const region of regions) {
    const layer = document.createElement("canvas"); layer.width = canvas.width; layer.height = canvas.height; const layerContext = layer.getContext("2d"); if (!layerContext) continue;
    layerContext.lineCap = "round"; layerContext.lineJoin = "round";
    region.polygons.forEach((polygon, polygonIndex) => {
      if (polygon.length < 3) return; const flowPath = region.flowPaths[polygonIndex] ?? [];
      layerContext.save(); layerContext.beginPath(); polygon.forEach((point, index) => { const x = point.x * scale; const y = point.y * scale; if (index) layerContext.lineTo(x, y); else layerContext.moveTo(x, y); }); layerContext.closePath(); layerContext.clip();
      layerContext.fillStyle = flowColor(flowPath, region.waterType, region.movement, region.opacity, region.flowDirection); layerContext.fillRect(0, 0, layer.width, layer.height);
      if (region.waterType === "river" && flowPath.length >= 2) {
        layerContext.lineWidth = Math.max(2, (region.brushRadii[polygonIndex] ?? 12) * scale * 2.25);
        for (let index = 1; index < flowPath.length; index += 1) { const segment = [flowPath[index - 1], flowPath[index]]; layerContext.strokeStyle = flowColor(segment, "river", region.movement, region.opacity, region.flowDirection); layerContext.beginPath(); layerContext.moveTo(segment[0].x * scale, segment[0].y * scale); layerContext.lineTo(segment[1].x * scale, segment[1].y * scale); layerContext.stroke(); }
      }
      layerContext.restore();
    });
    if (region.erasePolygons.length) {
      layerContext.globalCompositeOperation = "destination-out"; layerContext.fillStyle = "#000";
      for (const polygon of region.erasePolygons) { layerContext.beginPath(); polygon.forEach((point, index) => { const x = point.x * scale; const y = point.y * scale; if (index) layerContext.lineTo(x, y); else layerContext.moveTo(x, y); }); layerContext.closePath(); layerContext.fill(); }
    }
    context.drawImage(layer, 0, 0);
  }
  return canvas;
}

export function WaterRegions({ regions, weatherDryPolygons = [], bounds, rain = "off", paused = false, projectionMask }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const validRegions = useMemo(() => regions.map((region) => ({ ...region, polygons: region.polygons.filter((polygon) => polygon.length >= 3) })).filter((region) => region.polygons.length), [regions]);
  const validDryPolygons = useMemo(() => weatherDryPolygons.filter((polygon) => polygon.length >= 3), [weatherDryPolygons]);
  const waterRegionCount = validRegions.length; const waterShapeCount = validRegions.reduce((count, region) => count + region.polygons.length, 0);
  const hasWater = waterRegionCount > 0;
  const boundsRef = useRef(bounds); boundsRef.current = bounds;
  const rainRef = useRef(rain); rainRef.current = rain;
  const redrawRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !hasWater) return;
    canvas.dataset.waterSetupCount = String(Number(canvas.dataset.waterSetupCount ?? 0) + 1);
    const maxBufferSide = 2048; const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const gl = canvas.getContext("webgl", { alpha: true, antialias: true, premultipliedAlpha: true });
    if (!gl) return;
    let vertex: WebGLShader; let fragment: WebGLShader;
    try { vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER); fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER); }
    catch (error) { canvas.dataset.waterRenderer = "unavailable"; canvas.dataset.waterError = error instanceof Error ? error.message : "Water shader failed."; console.warn("Animated water is unavailable:", error); return; }
    const program = gl.createProgram(); const weatherMaskTexture = gl.createTexture(); const waterSettingsTexture = gl.createTexture();
    if (!program || !weatherMaskTexture || !waterSettingsTexture) {
      if (weatherMaskTexture) gl.deleteTexture(weatherMaskTexture); if (waterSettingsTexture) gl.deleteTexture(waterSettingsTexture);
      if (program) gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); return;
    }
    gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      canvas.dataset.waterRenderer = "unavailable"; canvas.dataset.waterError = gl.getProgramInfoLog(program) ?? "Water shader failed to link.";
      gl.deleteTexture(weatherMaskTexture); gl.deleteTexture(waterSettingsTexture); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); return;
    }
    const buffer = gl.createBuffer(); gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.useProgram(program); gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);
    const position = gl.getAttribLocation(program, "a_position"); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    const time = gl.getUniformLocation(program, "u_time"); const resolution = gl.getUniformLocation(program, "u_resolution");
    const rainAmount = gl.getUniformLocation(program, "u_rain"); const weatherMask = gl.getUniformLocation(program, "u_weather_mask"); const waterSettings = gl.getUniformLocation(program, "u_water_settings");
    gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, weatherMaskTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(weatherMask, 0);
    gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, waterSettingsTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.uniform1i(waterSettings, 1);
    let frame = 0; let frameTimer: number | undefined;
    let maskWidth = -1; let maskHeight = -1;
    const schedule = () => {
      if (paused) return;
      frameTimer = window.setTimeout(() => { frameTimer = undefined; frame = requestAnimationFrame(draw); }, 1000 / 20);
    };
    const draw = (now: number) => {
      const currentBounds = boundsRef.current;
      const cssWidth = canvas.clientWidth || currentBounds.width; const cssHeight = canvas.clientHeight || currentBounds.height;
      const bufferScale = Math.min(dpr, maxBufferSide / Math.max(cssWidth, cssHeight, 1));
      const width = Math.max(1, Math.round(cssWidth * bufferScale)); const height = Math.max(1, Math.round(cssHeight * bufferScale));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; }
      if (maskWidth !== Math.round(cssWidth) || maskHeight !== Math.round(cssHeight)) {
        maskWidth = Math.round(cssWidth); maskHeight = Math.round(cssHeight);
        gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, weatherMaskTexture); gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, drawWeatherMask(validDryPolygons, cssWidth, cssHeight));
        gl.activeTexture(gl.TEXTURE1); gl.bindTexture(gl.TEXTURE_2D, waterSettingsTexture);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, drawWaterSettings(validRegions, cssWidth, cssHeight));
      }
      gl.viewport(0, 0, width, height); gl.clearColor(0, 0, 0, 0); gl.clear(gl.COLOR_BUFFER_BIT);
      gl.uniform2f(resolution, width, height);
      gl.uniform1f(time, paused ? 2.4 : now / 1000); gl.uniform1f(rainAmount, RAIN_REACTION[rainRef.current]); gl.drawArrays(gl.TRIANGLES, 0, 6); canvas.dataset.renderedFrame = "true"; canvas.dataset.waterRenderer = "webgl"; canvas.dataset.rainReactionRenderer = "webgl";
      schedule();
    };
    redrawRef.current = () => draw(performance.now() + 33);
    draw(performance.now());
    return () => { redrawRef.current = () => undefined; cancelAnimationFrame(frame); if (frameTimer !== undefined) clearTimeout(frameTimer); gl.deleteTexture(weatherMaskTexture); gl.deleteTexture(waterSettingsTexture); gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); };
  }, [hasWater, paused, validDryPolygons, validRegions]);
  useEffect(() => { if (hasWater && paused) redrawRef.current(); }, [bounds.height, bounds.width, hasWater, paused, rain]);

  if (!waterRegionCount) return null;
  const waterTypes = [...new Set(validRegions.map((region) => region.waterType))].join(",");
  return <div className="water-regions-layer" style={{ left: bounds.left, top: bounds.top, width: bounds.width, height: bounds.height, maskImage: projectionMask, WebkitMaskImage: projectionMask, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }} data-water-region-count={waterRegionCount} data-water-shape-count={waterShapeCount} data-water-erasure-count={validRegions.reduce((count, region) => count + region.erasePolygons.length, 0)} data-water-types={waterTypes} data-water-movement={validRegions.map((region) => region.movement.toFixed(2)).join(",")} data-water-opacity={validRegions.map((region) => region.opacity.toFixed(2)).join(",")} data-water-flow-directions={validRegions.map((region) => Math.round(region.flowDirection)).join(",")} data-river-count={validRegions.filter((region) => region.waterType === "river").length} data-flow-model="direction-wheel" data-water-boundary="brush-union-with-eraser" data-animated={paused ? "false" : "true"} data-rain-reaction={rain === "off" ? "off" : "ripples"} data-rain-level={rain} data-rain-strength={RAIN_REACTION[rain]} data-weather-dry-mask={validDryPolygons.length ? "active" : "none"} data-projection-mask={projectionMask ? "active" : "none"}>
    <canvas ref={canvasRef} aria-hidden="true" data-water-palette="fixed-water-texture-v2" />
  </div>;
}
